import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Helmet } from 'react-helmet-async'
import { api } from '@/services/api'
import { getRedtubeVideoDetails, parseVideoId, normalizeRedtubeVideo } from '@/services/adultContent'
import { type Content } from '@/services/homepage'
import './ContentPage.css'

interface RedtubeThumb {
  size: string
  width: number
  height: number
  src: string
}

interface RedtubeTag {
  tag_name: string
}

// Interface pour les vidéos Redtube de la recherche
interface RedtubeVideoSearch {
  video_id?: string | number
  title?: string
  duration?: string
  views?: number
  thumb?: string
  default_thumb?: string
}

interface RedtubeVideoData {
  duration: string
  views: number
  video_id: string
  rating: number
  ratings: number
  title: string
  url: string
  embed_url: string
  default_thumb: string
  thumb: string
  publish_date: string
  thumbs: RedtubeThumb[]
  tags?: RedtubeTag[]
}

interface RedtubeVideoDetails {
  video: RedtubeVideoData
}

const RedtubeContentPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const parsedId = id ? parseVideoId(id) : null

  // Requête pour récupérer les détails de la vidéo
  const { data: videoData, isLoading, error } = useQuery({
    queryKey: ['redtube-video', parsedId?.videoId],
    queryFn: async () => {
      if (!parsedId?.videoId) throw new Error('ID de vidéo manquant')
      return await getRedtubeVideoDetails(parsedId.videoId) as RedtubeVideoDetails
    },
    enabled: !!parsedId?.videoId && !!parsedId,
  })

  // Fonction pour formater la durée
  const formatDuration = (duration?: string): string => {
    if (!duration) return ''
    return duration // Format déjà "MM:SS"
  }

  // Fonction pour extraire l'URL src d'un embed_url
  const extractIframeSrc = (embedUrl: string): string => {
    // embed_url est déjà une URL complète
    if (embedUrl.startsWith('http://') || embedUrl.startsWith('https://')) {
      return embedUrl
    }
    // Sinon, essayer d'extraire depuis un code embed
    const srcMatch = embedUrl.match(/src=["']([^"']+)["']/i)
    return srcMatch && srcMatch[1] ? srcMatch[1] : embedUrl
  }

  // Extraire les tags et l'ID de la vidéo actuelle
  // Les tags sont dans video.tags selon la structure JSON
  const tags = videoData?.video?.tags || []
  const currentVideoId = videoData?.video?.video_id

  // Debug: vérifier les tags
  console.log('RedtubeContentPage - Tags:', tags)
  console.log('RedtubeContentPage - Current Video ID:', currentVideoId)

  // Requête pour récupérer les vidéos suggérées basées sur les tags
  const { data: suggestedVideos, isLoading: isSuggestedLoading, error: suggestedError } = useQuery({
    queryKey: ['redtube-suggested', tags, currentVideoId],
    queryFn: async () => {
      if (!tags || tags.length === 0) {
        console.log('RedtubeContentPage - Aucun tag disponible pour la recherche')
        return []
      }
      
      // Prendre les 3 premiers tags pour la recherche
      const searchTags = tags.slice(0, 3).map(tag => tag.tag_name).join(' ')
      console.log('RedtubeContentPage - Recherche avec tags:', searchTags)
      
      try {
        const response = await api.get('/adult-content/redtube/search', {
          params: {
            search: searchTags,
            page: 1,
          },
        })
        
        console.log('RedtubeContentPage - Réponse API:', response.data)
        const videos = response.data?.videos || response.data?.data || response.data || []
        console.log('RedtubeContentPage - Vidéos trouvées:', videos.length)
        
        // Filtrer la vidéo actuelle et normaliser les résultats
        const filteredVideos = videos
          .filter((videoSearch: RedtubeVideoSearch) => {
            const videoId = videoSearch.video_id?.toString()
            return videoId !== currentVideoId?.toString()
          })
          .map((videoSearch: RedtubeVideoSearch, index: number) => {
            // Normaliser au format attendu par normalizeRedtubeVideo
            const normalizedVideo = {
              video_id: videoSearch.video_id?.toString(),
              title: videoSearch.title,
              duration: videoSearch.duration,
              views: videoSearch.views,
              thumb: videoSearch.thumb,
              default_thumb: videoSearch.default_thumb,
            }
            return normalizeRedtubeVideo(normalizedVideo as any, index)
          })
          .filter((video: Content | null): video is Content => video !== null)
          .slice(0, 12)
        
        console.log('RedtubeContentPage - Vidéos normalisées:', filteredVideos.length)
        return filteredVideos
      } catch (error) {
        console.error('Erreur lors de la récupération des vidéos suggérées:', error)
        return []
      }
    },
    enabled: !!videoData?.video?.tags && videoData.video.tags.length > 0,
  })

  console.log('RedtubeContentPage - Suggested videos:', suggestedVideos)
  console.log('RedtubeContentPage - Suggested loading:', isSuggestedLoading)
  console.log('RedtubeContentPage - Suggested error:', suggestedError)

  // Navigation vers une vidéo suggérée
  const handleSuggestedVideoClick = (video: Content) => {
    // Extraire l'ID depuis video.id (format: redtube_xxx) ou utiliser providerVideoId
    const videoId = video.providerVideoId || 
                   (typeof video.id === 'string' ? video.id.replace('redtube_', '') : String(video.id)) ||
                   String(video.id)
    navigate(`/content/redtube_${videoId}`)
  }

  if (isLoading) {
    return <div className="content-page"><div className="content-page-loading">Chargement...</div></div>
  }

  if (error || !videoData || !videoData.video) {
    return <div className="content-page"><div className="content-page-error">Contenu non trouvé</div></div>
  }

  const video = videoData.video
  const location = useLocation()
  const pageTitle = `${video.title} - Redtube - EdenPlaisir.com`
  const pageDescription = `Regardez ${video.title} sur EdenPlaisir.com`
  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '').split('?')[0] || `https://www.edenplaisir.com/content/redtube/${id}`

  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="player" />
      </Helmet>
      
      <div className="content-page-header">
        <h1 className="content-page-title">{video.title}</h1>
      </div>

      {/* Métadonnées */}
      <div className="content-page-meta">
        {video.publish_date && (
          <div className="content-page-meta-item">
            <strong>Publié:</strong>
            <span>{new Date(video.publish_date).toLocaleDateString('fr-FR')}</span>
          </div>
        )}
        {video.views !== undefined && (
          <div className="content-page-meta-item">
            <strong>Vues:</strong>
            <span>{video.views.toLocaleString()}</span>
          </div>
        )}
        {video.rating !== undefined && video.ratings > 0 && (
          <div className="content-page-meta-item">
            <strong>Note:</strong>
            <span>{(video.rating / video.ratings).toFixed(1)}/5 ({video.ratings} votes)</span>
          </div>
        )}
        {video.duration && (
          <div className="content-page-meta-item">
            <strong>Durée:</strong>
            <span>{formatDuration(video.duration)}</span>
          </div>
        )}
      </div>

      {/* Layout principal avec vidéo et sidebar */}
      <div className="content-page-main">
        <div className="content-page-main-content">
          {/* Affichage de la vidéo */}
          {video.embed_url && (
            <div className="content-page-video-container">
              <iframe
                src={extractIframeSrc(video.embed_url)}
                width="100%"
                height="100%"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={video.title}
              />
            </div>
          )}
        </div>

        <div className="content-page-sidebar">
          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="content-page-tags">
              <h3>Tags</h3>
              <div className="content-page-tag-list">
                {video.tags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="content-page-tag clickable"
                    onClick={() => navigate('/categories', { state: { filterTag: tag.tag_name } })}
                  >
                    {tag.tag_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vidéos suggérées */}
      {suggestedVideos && suggestedVideos.length > 0 && (
        <div className="content-page-related">
          <h2 className="content-page-section-title">Vidéos similaires</h2>
          {isSuggestedLoading ? (
            <div className="content-page-loading">Chargement des suggestions...</div>
          ) : (
            <div className="content-page-related-grid">
              {suggestedVideos.map((video: Content) => (
                <div
                  key={video.id}
                  className="content-page-related-card"
                  onClick={() => handleSuggestedVideoClick(video)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="content-page-related-thumbnail">
                    {video.thumbnailS3Key && typeof video.thumbnailS3Key === 'string' && video.thumbnailS3Key.startsWith('http') ? (
                      <img
                        src={video.thumbnailS3Key}
                        alt={video.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    ) : video.thumbnailS3Key ? (
                      <img
                        src={`/api/contents/${video.id}/thumbnail`}
                        alt={video.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="video-thumbnail placeholder" style={{ display: 'flex' }}>
                        <div className="placeholder-content">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    )}
                    {video.duration && (
                      <div className="content-page-related-duration">
                        {typeof video.duration === 'number' 
                          ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}`
                          : video.duration}
                      </div>
                    )}
                  </div>
                  <div className="content-page-related-info">
                    <h4 className="content-page-related-title">{video.title}</h4>
                    {video.views !== undefined && (
                      <div className="content-page-related-rating">
                        {video.views.toLocaleString()} vues
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RedtubeContentPage

